import { createServerFn } from "@tanstack/react-start";
import { eq, desc, and, ilike, or } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { generateUniqueSlug } from "../utils/slug.server";
import { invalidatePageCache } from "./public.functions";
import { resolveMediaFields } from "../media/resolve";
import { parseCraftStateInput } from "../craft/parse-craft-input.server";

async function requireCraftPagesAdmin() {
  return requirePermission("pages", "manage");
}

const craftStateSchema = z.union([z.string(), z.record(z.unknown())]).optional().nullable();

const pageInput = z.object({
  title: z.string().min(1),
  slug: z.string().optional(),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  craftState: craftStateSchema,
  craftCustomCss: z.string().optional().nullable(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  ogImageMediaId: z.string().uuid().optional().nullable(),
  isSystemPage: z.boolean().optional(),
});

export const listPagesAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ search: z.string().optional(), status: z.string().optional() }).optional())
  .handler(async ({ data }) => {
    await requireCraftPagesAdmin();
    const db = getDb();
    const conditions = [];
    if (data?.status) {
      conditions.push(eq(schema.pages.status, data.status as "draft" | "published" | "archived"));
    }
    if (data?.search) {
      conditions.push(
        or(
          ilike(schema.pages.title, `%${data.search}%`),
          ilike(schema.pages.slug, `%${data.search}%`),
        )!,
      );
    }
    return db
      .select()
      .from(schema.pages)
      .where(conditions.length ? and(...conditions) : undefined)
      .orderBy(desc(schema.pages.updatedAt));
  });

export const getPageAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requireCraftPagesAdmin();
    const [row] = await getDb()
      .select()
      .from(schema.pages)
      .where(eq(schema.pages.id, data.id))
      .limit(1);
    return row ?? null;
  });

export const createPage = createServerFn({ method: "POST" })
  .inputValidator(pageInput)
  .handler(async ({ data }) => {
    const user = await requireCraftPagesAdmin();
    const db = getDb();
    const slug = data.slug || (await generateUniqueSlug(schema.pages, data.title));
    const og = await resolveMediaFields({ mediaId: data.ogImageMediaId, url: null });
    const [row] = await db
      .insert(schema.pages)
      .values({
        title: data.title,
        slug,
        status: data.status,
        craftState: parseCraftStateInput(data.craftState),
        craftCustomCss: data.craftCustomCss ?? null,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        ogImageMediaId: og.mediaId,
        isSystemPage: data.isSystemPage ?? false,
        publishedAt: data.status === "published" ? new Date() : null,
        createdBy: user.id,
        updatedBy: user.id,
      })
      .returning();
    await invalidatePageCache(slug);
    return row;
  });

export const updatePage = createServerFn({ method: "POST" })
  .inputValidator(pageInput.extend({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const user = await requireCraftPagesAdmin();
    const db = getDb();
    const [existing] = await db
      .select()
      .from(schema.pages)
      .where(eq(schema.pages.id, data.id))
      .limit(1);
    if (!existing) throw new Error("Not found");

    const slug = data.slug || existing.slug;
    const og = await resolveMediaFields({ mediaId: data.ogImageMediaId, url: null });
    const [row] = await db
      .update(schema.pages)
      .set({
        title: data.title,
        slug,
        status: data.status,
        craftState:
          data.craftState !== undefined
            ? parseCraftStateInput(data.craftState)
            : existing.craftState,
        craftCustomCss:
          data.craftCustomCss !== undefined ? data.craftCustomCss : existing.craftCustomCss,
        metaTitle: data.metaTitle,
        metaDescription: data.metaDescription,
        ogImageMediaId: og.mediaId,
        isSystemPage: data.isSystemPage ?? existing.isSystemPage,
        publishedAt:
          data.status === "published" ? (existing.publishedAt ?? new Date()) : existing.publishedAt,
        updatedBy: user.id,
        updatedAt: new Date(),
      })
      .where(eq(schema.pages.id, data.id))
      .returning();

    if (existing.slug !== slug) {
      await db.insert(schema.slugHistory).values({
        entityType: "page",
        entityId: data.id,
        oldSlug: existing.slug,
        newSlug: slug,
      });
    }
    await invalidatePageCache(existing.slug);
    await invalidatePageCache(slug);
    return row;
  });

export const deletePage = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requireCraftPagesAdmin();
    const db = getDb();
    const [existing] = await db
      .select()
      .from(schema.pages)
      .where(eq(schema.pages.id, data.id))
      .limit(1);
    await db.delete(schema.pages).where(eq(schema.pages.id, data.id));
    if (existing) await invalidatePageCache(existing.slug);
    return { ok: true };
  });

export const generatePageSlug = createServerFn({ method: "POST" })
  .inputValidator(z.object({ title: z.string(), excludeId: z.string().uuid().optional() }))
  .handler(async ({ data }) => {
    await requireCraftPagesAdmin();
    return generateUniqueSlug(schema.pages, data.title, data.excludeId);
  });
